<div class="mt-3">

	<div *ngIf="loading" class="text-center my-4">
		<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
	</div>

	<p *ngIf="!loading && message" class="text-muted">{{ message }}</p>

	<ng-container *ngIf="!loading && !message">

		<div class="feature-tile-row" *ngIf="summary">
			<app-kpi-tile tone="accent" label="items they reorder" [value]="summary.items"></app-kpi-tile>
			<app-kpi-tile tone="partial" label="due now" [value]="summary.due_now"></app-kpi-tile>
			<app-kpi-tile tone="failed" label="stopped buying" [value]="summary.lapsed"></app-kpi-tile>
			<app-kpi-tile tone="settled" label="spend (12 mo)" [value]="'$' + (summary.revenue_12mo | number:'1.0-0')"></app-kpi-tile>
		</div>

		<div class="feature-section mt-4" *ngIf="dueRows.length">
			<p class="feature-section__title">Due now</p>
			<span class="feature-section__hint">At or past their usual reorder date.</span>
			<table class="feature-table feature-table--responsive mt-2">
				<thead>
					<tr>
						<th>Item</th>
						<th>Why it's due</th>
						<th class="is-num">Suggest</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of dueRows">
						<td data-label="Item">
							<span>
								{{ r.code }}
								<div class="text-muted" style="font-size: 11px;">{{ r.item }}</div>
							</span>
						</td>
						<td data-label="Why it's due">
							<span>
								{{ buyingPattern(r) }}
								<div class="text-muted" style="font-size: 11px;">{{ whyDue(r) }}</div>
							</span>
						</td>
						<td data-label="Suggest" class="is-num">{{ r.suggested_qty | number:'1.0-2' }}</td>
						<td>
							<button mat-icon-button (click)="openHistory(r.code)"
								matTooltip="Show the orders behind this"
								[attr.aria-label]="'Show the orders behind ' + r.code">
								<i class="material-icons" aria-hidden="true">history</i>
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="feature-section mt-4" *ngIf="upcomingRows.length">
			<p class="feature-section__title">Coming up</p>
			<span class="feature-section__hint">Not due yet.</span>
			<table class="feature-table feature-table--responsive mt-2">
				<thead>
					<tr>
						<th>Item</th>
						<th>Buying pattern</th>
						<th class="is-num">Suggest</th>
						<th>Due</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of upcomingRows">
						<td data-label="Item">
							<span>
								{{ r.code }}
								<div class="text-muted" style="font-size: 11px;">{{ r.item }}</div>
							</span>
						</td>
						<td data-label="Buying pattern">{{ buyingPattern(r) }}</td>
						<td data-label="Suggest" class="is-num">{{ r.suggested_qty | number:'1.0-2' }}</td>
						<td data-label="Due">{{ r.next_due | date:'MMM d' }}</td>
						<td>
							<button mat-icon-button (click)="openHistory(r.code)"
								matTooltip="Show the orders behind this"
								[attr.aria-label]="'Show the orders behind ' + r.code">
								<i class="material-icons" aria-hidden="true">history</i>
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="feature-section mt-4" *ngIf="lapsedRows.length">
			<p class="feature-section__title">Stopped buying</p>
			<span class="feature-section__hint">Missed more than a full cycle — worth a call.</span>
			<table class="feature-table feature-table--responsive mt-2">
				<thead>
					<tr>
						<th>Item</th>
						<th>Used to buy</th>
						<th>Last bought</th>
						<th class="is-num">Spend<br>(12 mo)</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of lapsedRows">
						<td data-label="Item">
							<span>
								{{ r.code }}
								<div class="text-muted" style="font-size: 11px;">{{ r.item }}</div>
							</span>
						</td>
						<td data-label="Used to buy">every {{ r.cycle_days }} days</td>
						<td data-label="Last bought">{{ r.last_buy | date:'MMM d, y' }}</td>
						<td data-label="Spend (12 mo)" class="is-num">{{ r.revenue_12mo | currency }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<p *ngIf="!rows.length" class="text-center text-muted my-4">
			Nothing recurring yet — this customer needs 3+ purchases of the same item before a cycle can be read.
		</p>

	</ng-container>

</div>

<!-- Why this cycle: the orders it was measured from. Gap is the series the
     cycle averages, so it can be checked rather than trusted. -->
<ng-template #historyDialog>
	<h2 mat-dialog-title>{{ history?.item || 'Purchase history' }}</h2>

	<div mat-dialog-content>
		<div *ngIf="historyLoading" class="text-center my-4">
			<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
		</div>

		<ng-container *ngIf="!historyLoading && history">
			<p class="text-muted mb-2" style="font-size: 12px;">{{ history.item }}</p>

			<div class="feature-tile-row">
				<app-kpi-tile tone="accent" label="buys every" [value]="history.score.avg_gap + ' days'"></app-kpi-tile>
				<app-kpi-tile tone="default" label="times bought" [value]="history.score.buys"></app-kpi-tile>
				<app-kpi-tile tone="default" label="usual qty" [value]="history.score.avg_qty"></app-kpi-tile>
				<app-kpi-tile tone="settled" label="due" [value]="history.score.next_due | date:'MMM d'"></app-kpi-tile>
			</div>

			<p class="text-muted mt-2 mb-0" style="font-size: 11px;">
				Gap = days since the order above it. That's what the average is built from.
			</p>

			<table class="feature-table feature-table--responsive mt-3">
				<thead>
					<tr>
						<th>Date</th>
						<th class="is-num">Gap</th>
						<th>Order</th>
						<th>Invoice</th>
						<th class="is-num">Qty</th>
						<th class="is-num">Price</th>
						<th class="is-num">Total</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let h of history.rows">
						<td data-label="Date">{{ h.orddate | date:'MMM d, y' }}</td>
						<td data-label="Gap" class="is-num">{{ h.gap_days ? h.gap_days + 'd' : '—' }}</td>
						<td data-label="Order">{{ h.orders }}</td>
						<td data-label="Invoice">{{ h.invoices || '—' }}</td>
						<td data-label="Qty" class="is-num">{{ h.qty | number:'1.0-2' }}</td>
						<td data-label="Price" class="is-num">{{ h.unitprice | currency }}</td>
						<td data-label="Total" class="is-num">{{ h.ext | currency }}</td>
					</tr>
					<tr *ngIf="!history.rows.length">
						<td colspan="7" class="text-center text-muted">No orders in the scoring window</td>
					</tr>
				</tbody>
			</table>
		</ng-container>

		<p *ngIf="!historyLoading && !history" class="text-muted text-center my-4">
			Could not load the purchase history.
		</p>
	</div>

	<div mat-dialog-actions class="d-flex justify-content-end">
		<button mat-stroked-button mat-dialog-close>Close</button>
	</div>
</ng-template>
